import { flatten } from 'lodash'
import { generatePath } from 'react-router-dom-v5-compat'
import { contractorPaymentsItems } from 'shared/components/Onboarding/OnboardingTaskList'
import { TaskStatusEnum } from 'shared/components/TaskList/TaskList'
import {
  OnboardingChecklist,
  OnboardingChecklistName,
  OnboardingSubChecklist,
} from 'shared/models/onboarding'

import { getTruckLeaseApplicationPath } from '../pages/Onboarding/OnboardingTaskList.util'
import {
  ONBOARDING_ADD_DRIVER_PATH,
  ONBOARDING_APPLICATION_STATUS_PATH,
  ONBOARDING_BANK_ACCOUNT_PATH,
  ONBOARDING_CLEARINGHOUSE_PATH,
  ONBOARDING_CONFIRM_CT_CASH_PATH,
  ONBOARDING_DRIVER_APPLICATION_PATH,
  ONBOARDING_DRUG_SCREEN_PATH,
  ONBOARDING_ELD_SETUP_PATH,
  ONBOARDING_EMPLOYMENT_HISTORY,
  ONBOARDING_EQUIPMENT_ADD_TRAILER_PATH,
  ONBOARDING_EQUIPMENT_ADD_TRUCK_PATH,
  ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_DOT_INSPECTION,
  ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_PLATES,
  ONBOARDING_FLEET_DETAILS_PATH,
  ONBOARDING_FUEL_CARD_PATH,
  ONBOARDING_INSURANCE_BENEFICIARY_PATH,
  ONBOARDING_INSURANCE_SETUP_PATH,
  ONBOARDING_ONBOARDING_WEBINAR_PATH,
  ONBOARDING_SIGN_LEASE_AGREEMENT_AMENDMENT_PATH,
  ONBOARDING_SIGN_LEASE_AGREEMENT_PATH,
  ONBOARDING_TASK_LIST_PATH,
  ONBOARDING_TRUCK_DECALS_PATH,
  ONBOARDING_TRUCK_LEASE_APPLICATION,
  ONBOARDING_TRUCK_LEASE_APPLICATION_TRUCK_DETAILS,
  ONBOARDING_VERIFY_STRIPE_PROFILE_PATH,
  ONBOARDING_WELCOME_PACKAGE_PATH,
} from '../Routes'

const TASK_PATH_MAP: Record<string, string[]> = {
  [OnboardingChecklistName.ADD_DRIVERS]: [ONBOARDING_ADD_DRIVER_PATH],
  [OnboardingChecklistName.DRIVER_APPLICATION]: [ONBOARDING_DRIVER_APPLICATION_PATH],
  [OnboardingChecklistName.FLEET_DETAILS]: [ONBOARDING_FLEET_DETAILS_PATH],
  [OnboardingChecklistName.EQUIPMENT_SETUP]: [
    ONBOARDING_EQUIPMENT_ADD_TRUCK_PATH,
    ONBOARDING_EQUIPMENT_ADD_TRAILER_PATH,
  ],
  [OnboardingChecklistName.ADD_INDIVIDUAL_EQUIPMENT]: [ONBOARDING_EQUIPMENT_ADD_TRUCK_PATH],
  [OnboardingChecklistName.SIGN_AGREEMENT]: [ONBOARDING_SIGN_LEASE_AGREEMENT_PATH],
  [OnboardingChecklistName.SIGN_AGREEMENT_AMENDMENT]: [ONBOARDING_SIGN_LEASE_AGREEMENT_AMENDMENT_PATH],
  [OnboardingChecklistName.BACKGROUND_CHECK]: [ONBOARDING_DRUG_SCREEN_PATH],
  [OnboardingChecklistName.CLEARINGHOUSE_QUERY]: [ONBOARDING_CLEARINGHOUSE_PATH],
  [OnboardingChecklistName.EMPLOYMENT_HISTORY]: [ONBOARDING_EMPLOYMENT_HISTORY],
  [OnboardingChecklistName.INSURANCE_BENEFICIARY]: [ONBOARDING_INSURANCE_BENEFICIARY_PATH],

  [OnboardingChecklistName.INSURANCE_SETUP]: [ONBOARDING_INSURANCE_SETUP_PATH],
  [OnboardingChecklistName.BANK_ACCOUNT_INFO]: [ONBOARDING_BANK_ACCOUNT_PATH],
  [OnboardingChecklistName.STRIPE_PAYMENT_PROFILE]: [ONBOARDING_VERIFY_STRIPE_PROFILE_PATH],
  [OnboardingChecklistName.CONFIRM_CT_CARD_INFO]: [ONBOARDING_CONFIRM_CT_CASH_PATH],
  [OnboardingChecklistName.FUEL_CARD]: [ONBOARDING_FUEL_CARD_PATH],
  [OnboardingChecklistName.WELCOME_PACKAGE]: [ONBOARDING_WELCOME_PACKAGE_PATH],
  [OnboardingChecklistName.ONBOARDING_WEBINAR]: [ONBOARDING_ONBOARDING_WEBINAR_PATH],
  [OnboardingChecklistName.ADD_INDIVIDUAL_INSURANCE_INFORMATION]: [ONBOARDING_INSURANCE_SETUP_PATH],
  [OnboardingChecklistName.TRUCK_LEASE_APPLICATION]: [ONBOARDING_TRUCK_LEASE_APPLICATION],
  [OnboardingChecklistName.ELD_SETUP]: [ONBOARDING_ELD_SETUP_PATH],
  [OnboardingChecklistName.TRUCK_DECALS]: [ONBOARDING_TRUCK_DECALS_PATH],
}

const PRE_BG_CHECK_TASKS: OnboardingChecklistName[] = [
  OnboardingChecklistName.ADD_DRIVERS,
  OnboardingChecklistName.DRIVER_APPLICATION,
  OnboardingChecklistName.EQUIPMENT_SETUP,
  OnboardingChecklistName.FLEET_DETAILS,
]
const getPreBgCheckTasks = (isL2o?: boolean) => {
  if (isL2o) {
    return [...PRE_BG_CHECK_TASKS, OnboardingChecklistName.EMPLOYMENT_HISTORY]
  }
  return PRE_BG_CHECK_TASKS
}

const AFTER_BG_CHECK_TASKS: OnboardingChecklistName[] = [
  OnboardingChecklistName.SIGN_AGREEMENT,
  OnboardingChecklistName.BACKGROUND_CHECK,
  OnboardingChecklistName.CLEARINGHOUSE_QUERY,
  OnboardingChecklistName.TRUCK_LEASE_APPLICATION,
]

export const NON_REQUIRED_TASKS: OnboardingChecklistName[] = [OnboardingChecklistName.FUEL_CARD]

const IGNORED_TASKS: string[] = [OnboardingChecklistName.INITIAL_BACKGROUND_CHECK, ...contractorPaymentsItems]

function getTaskPath(task: OnboardingChecklist | OnboardingSubChecklist, step?: number) {
  if (task?.name === OnboardingChecklistName.PLATES) {
    return generatePath(ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_PLATES, {
      equipmentId: task?.context?.truck?.id!,
    })
  }

  if (task?.name === OnboardingChecklistName.DOT_INSPECTION) {
    return generatePath(ONBOARDING_EQUIPMENT_TRUCK_DOCUMENTS_PATH_DOT_INSPECTION, {
      equipmentId: task?.context?.truck?.id!,
    })
  }

  if (task?.name === OnboardingChecklistName.TRUCK_LEASE_APPLICATION) {
    const path = getTruckLeaseApplicationPath(task as OnboardingChecklist)
    if (path && !path[1]) return path[0]
  }

  if (!task) return `${ONBOARDING_TASK_LIST_PATH}?skip_welcome_back=1&skip_application_summary=1&no_task_path`

  const taskPaths = TASK_PATH_MAP[task.name]

  if (step != null && taskPaths) {
    return taskPaths[step]
  }

  return (
    TASK_PATH_MAP[task.name]?.[0] ??
    `${ONBOARDING_TASK_LIST_PATH}?skip_welcome_back=1&skip_application_summary=1&from_get_task`
  )
}

export const getNextTaskPath = (
  checklist?: readonly OnboardingChecklist[],
  currentTaskName?: OnboardingChecklistName,
  step?: number,
  isL2o?: boolean,
  isFleetFirst?: boolean
): string => {
  const filteredChecklist = checklist?.filter((c) => !IGNORED_TASKS.includes(c.name))
  const PRE_BG_CHECK_TASKS = getPreBgCheckTasks(isL2o)

  if (!filteredChecklist || filteredChecklist.length === 0) {
    return `${ONBOARDING_TASK_LIST_PATH}?skip_welcome_back=1&no_tasks_in_checklist`
  }

  if (!currentTaskName) {
    const firstIncompleteTask = filteredChecklist.filter(
      (t) => !isTaskCompletedOrBlocked(t) || t.subitems?.some((c) => !isTaskCompletedOrBlocked(c))
    )[0]

    if (firstIncompleteTask) {
      const firstIncompleteTaskIndex = filteredChecklist.findIndex((c) => c.name === firstIncompleteTask.name)
      currentTaskName =
        firstIncompleteTaskIndex > 0
          ? filteredChecklist[firstIncompleteTaskIndex - 1].name
          : firstIncompleteTask.name
    } else {
      return `${ONBOARDING_TASK_LIST_PATH}?skip_welcome_back=1&no_initial_task`
    }
  }

  const currentTask = filteredChecklist.find((c) => c.name === currentTaskName)
  const currentTaskIndex = filteredChecklist.findIndex((c) => c.name === currentTaskName)

  if (step != null) {
    const stepPath = getTaskPath(currentTask!, step)
    if (stepPath) return stepPath
  }

  // If current task has subitems, return the first incomplete subitem
  if (currentTask?.subitems?.length) {
    const nextSubTask = currentTask.subitems.find((c) => !isTaskCompletedOrBlocked(c))
    if (nextSubTask) {
      return getTaskPath(nextSubTask)
    }
  }

  if (currentTask && !isTaskCompletedOrBlocked(currentTask)) {
    return getTaskPath(currentTask)
  }

  // if the current task is the last task, or if the current task is not found, return the task list path
  if (currentTaskIndex === filteredChecklist.length - 1 || currentTaskIndex === -1) {
    if (isFleetFirst) {
      return ONBOARDING_TRUCK_LEASE_APPLICATION_TRUCK_DETAILS
    }
    return `${ONBOARDING_TASK_LIST_PATH}?skip_welcome_back=1&last_task`
  }

  const nextTask = filteredChecklist[currentTaskIndex + 1]
  // if the next task requires the initial background check, we need to redirect to the application status page
  if (
    currentTask &&
    tasksCompletedOrMissing(filteredChecklist, PRE_BG_CHECK_TASKS) &&
    PRE_BG_CHECK_TASKS.includes(currentTask.name) &&
    AFTER_BG_CHECK_TASKS.includes(nextTask.name) &&
    nextTask.status === TaskStatusEnum.PREREQUISITES_NOT_MET
  ) {
    return ONBOARDING_APPLICATION_STATUS_PATH
  }

  // If next task has subitems, return the first incomplete subitem
  if (nextTask?.subitems?.length) {
    const nextSubTask = nextTask.subitems.find((c) => !isTaskCompletedOrBlocked(c))
    if (nextSubTask) {
      return getTaskPath(nextSubTask)
    }
  }

  if (isTaskCompletedOrBlocked(nextTask)) {
    return getNextTaskPath(checklist, nextTask.name, step, isL2o)
  }

  return getTaskPath(nextTask)
}

function tasksCompletedOrMissing(
  checklist: readonly OnboardingChecklist[],
  tasks: OnboardingChecklistName[]
) {
  return tasks
    .map((t) => {
      const tt = checklist.find((c) => c.name === t)
      return !tt || isTaskCompletedOrBlocked(tt)
    })
    .every(Boolean)
}

const TASKS_COMPLEXITY: Partial<Record<OnboardingChecklistName, number>> = {
  [OnboardingChecklistName.DRIVER_APPLICATION]: 6,
  [OnboardingChecklistName.FLEET_DETAILS]: 2,
  [OnboardingChecklistName.EQUIPMENT_SETUP]: 2,
  [OnboardingChecklistName.SIGN_AGREEMENT]: 2,
  [OnboardingChecklistName.EMPLOYMENT_HISTORY]: 2,
  [OnboardingChecklistName.INSURANCE_SETUP]: 2,
  [OnboardingChecklistName.PAYMENT_PROFILE_SETUP]: 3,
  [OnboardingChecklistName.FUEL_CARD]: 5,
  [OnboardingChecklistName.ADD_DRIVERS]: 2,
}

function getTaskComplexity(
  taskName: OnboardingChecklistName | null,
  checklist: readonly OnboardingChecklist[] | null
) {
  if (!taskName) return 1
  return (
    checklist?.find((task) => task.name === taskName)?.subitems?.length || TASKS_COMPLEXITY[taskName] || 1
  )
}

function isTaskCompletedOrBlocked(task: OnboardingChecklist | OnboardingSubChecklist, includeBlocked = true) {
  if (isTaskSkipped(task.clientId)) return true

  if (task.name === OnboardingChecklistName.STRIPE_PAYMENT_PROFILE) {
    return task.status === TaskStatusEnum.COMPLETED
  }

  if (task.name === OnboardingChecklistName.TRUCK_SETUP && !(task as OnboardingChecklist).subitems?.length) {
    return true
  }

  if (includeBlocked && task.isDisabled) return true

  // truck lease is special case where user has to return to the task multiple times
  if (
    task.name === OnboardingChecklistName.TRUCK_LEASE_APPLICATION &&
    [TaskStatusEnum.IN_PROGRESS, TaskStatusEnum.ERROR].includes(task.status)
  ) {
    if (task.status === TaskStatusEnum.IN_PROGRESS && !task.statusDetail) {
      return true
    }
    const path = getTruckLeaseApplicationPath(task as OnboardingChecklist)
    if (path) return path[1]
    return true
  }

  return [
    TaskStatusEnum.IN_PROGRESS,
    TaskStatusEnum.COMPLETED,
    includeBlocked ? TaskStatusEnum.PREREQUISITES_NOT_MET : null,
  ].includes(task.status)
}

export const getOnboardingProgressPercentage = (
  checklist: readonly OnboardingChecklist[] | null,
  taskName: OnboardingChecklistName | null,
  step: number | null,
  min: number,
  max: number
) => {
  const totalPercentage = max - min
  const totalSteps =
    checklist
      ?.map((task) => getTaskComplexity(task.name, checklist))
      .reduce((partialSum, a) => partialSum + a, 0) || 1
  const oneStepPercentage = totalPercentage / totalSteps

  const completedSteps =
    checklist
      ?.filter((task) => isTaskCompletedOrBlocked(task, false) && !task.subitems?.length)
      ?.map((task) => getTaskComplexity(task.name, checklist))
      .reduce((partialSum, a) => partialSum + a, 0) || 0

  const completedSubSteps =
    flatten(checklist?.filter((task) => task.subitems?.length)?.map((task) => task.subitems))
      ?.filter((task) => isTaskCompletedOrBlocked(task, false))
      ?.map((task) => getTaskComplexity(task.name, checklist))
      .reduce((partialSum, a) => partialSum + a, 0) || 0

  const currentTaskIsIncomplete = checklist?.find(
    (task) =>
      !isTaskCompletedOrBlocked(task, false) &&
      task.name === taskName &&
      (!task.subitems?.length || task.subitems.every((t) => isTaskCompletedOrBlocked(t, false)))
  )

  const stepCount = currentTaskIsIncomplete && step ? step : 0
  const percentage = min + oneStepPercentage * (completedSteps + completedSubSteps + stepCount)

  return Math.min(max, Math.max(Math.ceil(percentage), min))
}

const skippedTasks = new Set<string>()

export const addSkippedTask = (taskId: string) => {
  skippedTasks.add(taskId)
}

const isTaskSkipped = (taskId?: string) => {
  if (!taskId) return false
  return skippedTasks.has(taskId)
}
